import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  BusinessDataType,
  SingleUserProfileType,
  RecurringPlanStatusEnum,
} from '@am3nd-org/pointcraft-types';

import { updateBusinessConfig } from 'src/utils/post/account';

import { type Employee } from '../schemas/employee';
import { storeEvents, STORE_EVENTS } from '../utils';
import type { RecurringPlan, BusinessProfile } from '../types';
import { employeeApi, subscriptionPlanApi } from '../utils/api';
import type { CreateRecurringPlanDTO } from '../schemas/recurringPlan';
import { mapUsersToEmployees, mapBusinessToBusinessProfile } from '../utils/mappers';

/* ====================== SETTINGS STORE STATE ====================== */
interface SettingsStoreState {
  // STATES
  plans: RecurringPlan[];
  employees: Employee[];
  businessProfile: BusinessProfile | null;
  isLoading: boolean;
  error: string | null;
  employeeLoading: boolean;
  businessProfileLoading: boolean;
  plansLoading: boolean;

  // RECURRING PLANS OPERATIONS (Sync - for local state)
  setPlans: (plans: RecurringPlan[]) => void;
  getPlans: () => RecurringPlan[];
  getPlanById: (id: string) => RecurringPlan | undefined;

  // RECURRING PLANS OPERATIONS (Async API)
  fetchPlansAsync: () => Promise<void>;
  createPlanAsync: (
    plan: CreateRecurringPlanDTO
  ) => Promise<{ success: boolean; error?: string }>;
  updatePlanStatusAsync: (
    id: string,
    status: RecurringPlanStatusEnum
  ) => Promise<{ success: boolean; error?: string }>;

  // EMPLOYEES OPERATIONS (LOCAL)
  getEmployees: () => Employee[];
  getEmployeeById: (id: string) => Employee | undefined;
  createEmployee: (employee: Omit<Employee, 'id'>) => Employee;
  updateEmployee: (id: string, updates: Partial<Employee>) => void;
  deleteEmployee: (id: string) => void;

  // EMPLOYEES OPERATIONS (ASYNC API)
  createEmployeeAsync: (
    employee: Omit<Employee, 'id' | 'joinedDate'>
  ) => Promise<{ success: boolean; error?: string }>;
  updateEmployeeAsync: (
    id: string,
    updates: Partial<Employee>
  ) => Promise<{ success: boolean; error?: string }>;
  deleteEmployeeAsync: (id: string) => Promise<{ success: boolean; error?: string }>;

  // BUSINESS PROFILE OPERATIONS (LOCAL)
  getBusinessProfile: () => BusinessProfile | null;
  updateBusinessProfile: (updates: Partial<BusinessProfile>) => void;

  // BUSINESS PROFILE OPERATIONS (ASYNC API)
  updateBusinessProfileAsync: (
    updates: Partial<BusinessProfile>
  ) => Promise<{ success: boolean; error?: string }>;

  // SYNC OPERATIONS (from useUserDataStore)
  syncEmployeesFromUserStore: (users: SingleUserProfileType[]) => void;
  syncBusinessProfileFromUserStore: (business: BusinessDataType) => void;

  // UTILITIES
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

/* ====================== SETTINGS STORE ====================== */
export const useSettingStore = create<SettingsStoreState>()(
  devtools(
    (set, get) => ({
      // INITIAL STATE
      plans: [] as RecurringPlan[], // Fetched from API
      employees: [] as Employee[], // Synced from useUserDataStore
      businessProfile: null, // Synced from useUserDataStore
      isLoading: false,
      error: null,
      employeeLoading: false,
      businessProfileLoading: false,
      plansLoading: false,

      // RECURRING PLANS OPERATIONS (Sync - for local state)
      setPlans: (plans) => set({ plans }),

      getPlans: () => get().plans,

      getPlanById: (id) => get().plans.find((plan) => plan.id === id),

      // RECURRING PLANS OPERATIONS (Async API)
      fetchPlansAsync: async () => {
        set({ plansLoading: true, error: null });
        try {
          const response = await subscriptionPlanApi.getAll();
          if (response.success && response.data) {
            set({ plans: response.data, plansLoading: false });
          } else {
            const errorMessage = response.error?.details?.message || 'Failed to fetch plans';
            set({ plansLoading: false, error: errorMessage });
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch plans';
          set({ plansLoading: false, error: errorMessage });
        }
      },

      createPlanAsync: async (planData) => {
        set({ plansLoading: true, error: null });
        try {
          const response = await subscriptionPlanApi.create(planData);
          if (response.success) {
            // Refetch plans to get the created plan with server-generated fields
            await get().fetchPlansAsync();
            storeEvents.emit(STORE_EVENTS.PLAN_CREATED, planData);
            return { success: true };
          }
          const errorMessage = response.error?.details?.message || 'Failed to create plan';
          set({ plansLoading: false, error: errorMessage });
          return { success: false, error: errorMessage };
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to create plan';
          set({ plansLoading: false, error: errorMessage });
          return { success: false, error: errorMessage };
        }
      },

      updatePlanStatusAsync: async (id, status) => {
        set({ plansLoading: true, error: null });
        try {
          const response = await subscriptionPlanApi.updateStatus(id, status);
          if (response.success) {
            // Update local state
            set((state) => ({
              plans: state.plans.map((plan) =>
                plan.id === id ? { ...plan, status } : plan
              ),
              plansLoading: false,
            }));

            // EMIT EVENTS
            const updatedPlan = get().getPlanById(id);
            if (updatedPlan) {
              storeEvents.emit(STORE_EVENTS.PLAN_UPDATED, updatedPlan);

              // CHECK FOR STATUS CHANGES
              if (status === RecurringPlanStatusEnum.ACTIVE) {
                storeEvents.emit(STORE_EVENTS.PLAN_ACTIVATED, updatedPlan);
              } else if (status === RecurringPlanStatusEnum.CANCELLED) {
                storeEvents.emit(STORE_EVENTS.PLAN_CANCELLED, updatedPlan);
              }
            }
            return { success: true };
          }
          const errorMessage = response.error?.details?.message || 'Failed to update plan';
          set({ plansLoading: false, error: errorMessage });
          return { success: false, error: errorMessage };
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to update plan';
          set({ plansLoading: false, error: errorMessage });
          return { success: false, error: errorMessage };
        }
      },

      // EMPLOYEES OPERATIONS
      getEmployees: () => get().employees,

      getEmployeeById: (id) => get().employees.find((employee) => employee.id === id),

      createEmployee: (employee) => {
        const newEmployee: Employee = {
          ...employee,
          id: `emp_${crypto.randomUUID() as string}`,
          joinedDate: new Date(),
        };

        set((state) => ({
          employees: [...state.employees, newEmployee],
        }));

        // EMIT EVENT FOR NEW EMPLOYEE
        storeEvents.emit(STORE_EVENTS.EMPLOYEE_CREATED, newEmployee);

        return newEmployee;
      },

      updateEmployee: (id, updates) => {
        set((state) => ({
          employees: state.employees.map((employee) =>
            employee.id === id ? { ...employee, ...updates } : employee
          ),
        }));

        // EMIT EVENT FOR UPDATED EMPLOYEE
        const updatedEmployee = get().getEmployeeById(id);
        if (updatedEmployee) {
          storeEvents.emit(STORE_EVENTS.EMPLOYEE_UPDATED, updatedEmployee);
        }
      },

      deleteEmployee: (id: string) => {
        const deletedEmployee = get().getEmployeeById(id);

        set((state) => ({
          employees: state.employees.filter((employee) => employee.id !== id),
        }));

        // EMIT EVENT FOR DELETED EMPLOYEE
        if (deletedEmployee) {
          storeEvents.emit(STORE_EVENTS.EMPLOYEE_DELETED, deletedEmployee);
        }
      },

      // EMPLOYEES ASYNC API OPERATIONS
      createEmployeeAsync: async (employeeData) => {
        set({ employeeLoading: true, error: null });
        try {
          const response = await employeeApi.create(employeeData);
          if (response.success && response.data) {
            // Add to local store after successful API call
            set((state) => ({
              employees: [...state.employees, response.data],
              employeeLoading: false,
            }));
            storeEvents.emit(STORE_EVENTS.EMPLOYEE_CREATED, response.data);
            return { success: true };
          }
          const errorMessage = response.error?.details?.message || 'Failed to create employee';
          set({ employeeLoading: false, error: errorMessage });
          return { success: false, error: errorMessage };
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to create employee';
          set({ employeeLoading: false, error: errorMessage });
          return { success: false, error: errorMessage };
        }
      },

      updateEmployeeAsync: async (id, updates) => {
        set({ employeeLoading: true, error: null });
        try {
          const response = await employeeApi.update(id, updates);
          if (response.success && response.data) {
            // Update local store after successful API call
            set((state) => ({
              employees: state.employees.map((emp) =>
                emp.id === id ? { ...emp, ...response.data } : emp
              ),
              employeeLoading: false,
            }));
            storeEvents.emit(STORE_EVENTS.EMPLOYEE_UPDATED, response.data);
            return { success: true };
          }
          const errorMessage = response.error?.details?.message || 'Failed to update employee';
          set({ employeeLoading: false, error: errorMessage });
          return { success: false, error: errorMessage };
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to update employee';
          set({ employeeLoading: false, error: errorMessage });
          return { success: false, error: errorMessage };
        }
      },

      deleteEmployeeAsync: async (id) => {
        set({ employeeLoading: true, error: null });
        try {
          const deletedEmployee = get().getEmployeeById(id);
          const response = await employeeApi.delete(id);
          if (response.success) {
            // Remove from local store after successful API call
            set((state) => ({
              employees: state.employees.filter((emp) => emp.id !== id),
              employeeLoading: false,
            }));
            if (deletedEmployee) {
              storeEvents.emit(STORE_EVENTS.EMPLOYEE_DELETED, deletedEmployee);
            }
            return { success: true };
          }
          const errorMessage = response.error?.details?.message || 'Failed to delete employee';
          set({ employeeLoading: false, error: errorMessage });
          return { success: false, error: errorMessage };
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to delete employee';
          set({ employeeLoading: false, error: errorMessage });
          return { success: false, error: errorMessage };
        }
      },

      // BUSINESS PROFILE OPERATIONS
      getBusinessProfile: () => get().businessProfile,

      updateBusinessProfile: (profile) => {
        set((state) => ({
          businessProfile: { ...(state.businessProfile || {}), ...profile } as BusinessProfile,
        }));
      },

      // BUSINESS PROFILE ASYNC API OPERATIONS
      updateBusinessProfileAsync: async (updates) => {
        set({ businessProfileLoading: true, error: null });
        try {
          // Map companyName to name for the API
          const result = await updateBusinessConfig({
            name: updates.companyName,
            email: updates.email,
            phone: updates.phone,
            address: updates.address,
          });

          // Check for error in response
          if (result?.error) {
            set({ businessProfileLoading: false, error: result.error });
            return { success: false, error: result.error };
          }

          // Update local store on success
          set((state) => ({
            businessProfile: { ...(state.businessProfile || {}), ...updates } as BusinessProfile,
            businessProfileLoading: false,
          }));
          return { success: true };
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to update business profile';
          set({ businessProfileLoading: false, error: errorMessage });
          return { success: false, error: errorMessage };
        }
      },

      // SYNC OPERATIONS (from useUserDataStore)
      syncEmployeesFromUserStore: (users: SingleUserProfileType[]) => {
        const mappedEmployees = mapUsersToEmployees(users);
        set({ employees: mappedEmployees });
      },

      syncBusinessProfileFromUserStore: (business: BusinessDataType) => {
        const mappedProfile = mapBusinessToBusinessProfile(business);
        set({ businessProfile: mappedProfile });
      },

      // UTILITIES
      setLoading: (loading: boolean) => set({ isLoading: loading }),

      setError: (error: string | null) => set({ error }),
    }),
    { name: 'settings-store' }
  )
);
